import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Download, Share2, Globe, Lock, Copy, FileText, FileType, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { DeckSlide } from "@/components/decks/DeckSlide";
import { DECK } from "@/constants/testIds";
import api from "@/lib/api";

export default function PresentationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pres, setPres] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/presentations/${id}`);
      setPres(data);
      return data.status;
    } catch {
      toast.error("Could not load presentation.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    if (pres?.status === "generating") {
      pollRef.current = setInterval(async () => {
        const st = await load();
        if (st !== "generating") clearInterval(pollRef.current);
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [pres?.status, load]);

  const download = async (fmt) => {
    setDlBusy(true);
    try {
      const res = await api.get(`/presentations/${id}/download`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (fmt === "html") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(pres.title || "presentation").replace(/\s+/g, "_")}.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch {
      toast.error("Could not export the deck.");
    } finally {
      setDlBusy(false);
    }
  };

  const toggleShare = async (checked) => {
    const { data } = await api.patch(`/presentations/${id}`, { share_mode: checked ? "public" : "private" });
    setPres(data);
  };

  const publicUrl = pres?.share_token ? `${window.location.origin}/deck/${pres.share_token}` : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="p-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
      </div>
    );
  }
  if (!pres) return null;

  return (
    <div data-testid={DECK.detailRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        <button onClick={() => navigate("/present")} className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All presentations
        </button>

        {pres.status === "generating" && (
          <div data-testid={DECK.generating} className="mt-10 rounded-2xl border border-border/60 bg-card/50 p-10 text-center">
            <div className="relative mx-auto h-12 w-12">
              <Sparkles className="h-12 w-12 animate-pulse text-accent" />
            </div>
            <h2 className="mt-6 text-lg font-semibold">The analyst is designing your deck…</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Choosing analyses, running queries and writing talking points. This usually takes under a minute.
            </p>
            <p className="mt-4 text-xs italic text-muted-foreground">“{pres.prompt}”</p>
          </div>
        )}

        {pres.status === "error" && (
          <div className="mt-10 rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center">
            <h2 className="text-lg font-semibold text-red-400">Generation failed</h2>
            <p className="mt-2 text-sm text-muted-foreground">{pres.error || "Something went wrong."}</p>
            <Button variant="outline" onClick={() => navigate("/present")} className="mt-5 rounded-full text-xs">Back to presentations</Button>
          </div>
        )}

        {pres.status === "ready" && (
          <>
            <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{pres.title}</h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{pres.subtitle}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid={DECK.shareBtn} onClick={() => setShareOpen(true)} className="rounded-full text-xs">
                  {pres.share_mode === "public" ? <Globe className="mr-1.5 h-3.5 w-3.5 text-accent" /> : <Share2 className="mr-1.5 h-3.5 w-3.5" />}
                  Share
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" data-testid={DECK.downloadBtn} disabled={dlBusy} className="rounded-full bg-accent text-xs text-accent-foreground hover:bg-accent/90">
                      {dlBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
                      Download
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="font-mono">
                    <DropdownMenuItem data-testid={DECK.downloadPptx} onClick={() => download("pptx")}><FileType className="mr-2 h-4 w-4" /> PowerPoint (.pptx)</DropdownMenuItem>
                    <DropdownMenuItem data-testid={DECK.downloadPdf} onClick={() => download("pdf")}><FileText className="mr-2 h-4 w-4" /> PDF</DropdownMenuItem>
                    <DropdownMenuItem data-testid={DECK.downloadHtml} onClick={() => download("html")}><Code2 className="mr-2 h-4 w-4" /> HTML</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {pres.summary?.length > 0 && (
              <section data-testid={DECK.summary} className="mt-8 rounded-xl border border-accent/30 bg-accent/5 p-5">
                <h3 className="text-xs uppercase tracking-[0.16em] text-accent">Executive summary</h3>
                <ul className="mt-3 space-y-2">
                  {pres.summary.map((b, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-foreground/90">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />{b}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-6 space-y-4">
              {(pres.slides || []).map((s, i) => <DeckSlide key={i} slide={s} />)}
            </div>
          </>
        )}
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid={DECK.shareDialog} className="font-mono sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share presentation</DialogTitle>
            <DialogDescription>Make it public to share a read-only link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
              <span className="flex items-center gap-2 text-sm">
                {pres.share_mode === "public" ? <Globe className="h-4 w-4 text-accent" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                {pres.share_mode === "public" ? "Public — anyone with the link" : "Private — only your workspace"}
              </span>
              <Switch data-testid={DECK.shareToggle} checked={pres.share_mode === "public"} onCheckedChange={toggleShare} />
            </div>
            {pres.share_mode === "public" && publicUrl && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Public link</p>
                <div className="flex gap-2">
                  <Input data-testid={DECK.shareLink} readOnly value={publicUrl} className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} className="shrink-0">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
